<template>
  <div style="position: relative;" class="bg-white pl-4 pt-4 rounded">
    <!-- Header: Go Back and Edit/Save Actions -->
    <div v-if="!hideGoBack" class="d-flex justify-content-between" style="margin-bottom: 1.70rem;">
      <div class="d-flex" @click="goBack()">
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="38" viewBox="0 0 40 38" fill="none" style="cursor: pointer;">
          <path
            d="M38.75 19.0003H1.25M1.25 19.0003L18.9583 1.29199M1.25 19.0003L18.9583 36.7087"
            stroke="#FF7C48"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <div class="d-flex align-items-center" style="margin-left: 1.25rem;">
          <div>
            <h1 class="title">{{ $t("risk.detail") }}</h1>
          </div>
        </div>
      </div>
      <div>
        <div v-if="!isEditMode">
          <b-button v-if="canEdit" variant="primary" @click="enableEditMode"> <i class="bx bx-pencil font-size-16 align-middle"></i> {{ $t("common.edit") }} </b-button>
        </div>
        <div v-else>
          <b-button variant="success" class="mr-2" @click="save"> <i class="bx bx-check font-size-16 align-middle"></i> {{ $t("common.save") }} </b-button>
          <b-button variant="danger" @click="cancelEditMode"> <i class="bx bx-x font-size-16 align-middle"></i> {{ $t("common.cancel") }} </b-button>
        </div>
      </div>
    </div>

    <b-modal id="approval-request-modal" :title="$t('audit-control.approval.send')" @ok="handleApprovalRequestOk">
      <b-form-group :label="$t('common.approver')">
        <organization-member-select :selected="approvalRequest.assignee" @onSelect="(val) => (approvalRequest.assignee = val)" :placeholder="$t('common.approver')" />
      </b-form-group>
      <b-form-group :label="$t('audit-control.approval.approval.request-note-control')">
        <b-form-textarea v-model="approvalRequest.note" :placeholder="$t('common.enterNote')" rows="3"></b-form-textarea>
      </b-form-group>
    </b-modal>

    <b-modal id="close-risk-modal" :title="$t('risk.close')" @ok="handleCloseRiskOk">
      <b-form-group :label="$t('common.description')">
        <b-form-textarea v-model="closeNote" :placeholder="$t('common.description')" rows="3"></b-form-textarea>
      </b-form-group>
    </b-modal>

    <b-modal id="cancel-risk-modal" :title="$t('risk.cancel')" @ok="handleCancelRiskOk">
      <b-form-group :label="$t('common.description')">
        <b-form-textarea v-model="cancelNote" :placeholder="$t('common.description')" rows="3"></b-form-textarea>
      </b-form-group>
    </b-modal>

    <!-- Content Loading -->
    <content-loading v-if="isLoading && !risk._id" />
    <div v-else class="pr-4 pb-4" :class="{ 'opacity-50 pointer-events-none': isLoading }">
      <!-- Summary Header Section -->
      <div class="bg-light p-4 rounded mb-4 border relative" style="background-color: #e2e2e2; position: relative;">
        <div class="mr-3 text-right">
          <i
            class="bx bx-minus-circle text-secondary font-size-24 hand mr-2"
            v-if="risk.status !== 'CLOSED' && risk.status !== 'CANCELED'"
            @click="openCloseRiskModal"
            v-b-tooltip.hover
            :title="$t('risk.close')"
          ></i>
          <i
            class="bx bx-x-circle text-danger font-size-24 hand"
            v-if="risk.status !== 'CLOSED' && risk.status !== 'CANCELED'"
            @click="openCancelRiskModal"
            v-b-tooltip.hover
            :title="$t('risk.cancel')"
          ></i>
        </div>

        <div class="row">
          <div class="col-md-1 ">
            <div class="mb-2">
              <span class="font-weight-bold">{{ $t("risk.no") }} :</span>
              <div>
                <sweet-input :disableValidation="true" v-model="risk.no" disabled></sweet-input>
              </div>
            </div>
          </div>
          <div class="col-md-5"></div>
          <div class="col-md-6 text-right">
            <div class="mb-2">
              <span class="font-weight-bold">{{ $t("risk.status.text") }} :</span>
              <div>
                <b-badge :variant="statusVariant">{{ displayStatus }}</b-badge>
              </div>
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-12">
            <div class="text-break mb-3">
              <span class="font-weight-bold">{{ $t("risk.name") }} <span v-if="isEditMode" class="text-danger">*</span> :</span>
              <p v-if="!isEditMode" class="mb-0 mt-1 pl-">{{ risk.name | translate }}</p>
              <sweet-input :disableValidation="true" v-else v-model="localRisk.name" :rows="3" class="mt-2" />
            </div>

            <div class="text-break">
              <span class="font-weight-bold">{{ $t("risk.description") }} <span v-if="isEditMode" class="text-danger">*</span> :</span>
              <p v-if="!isEditMode" class="mb-0 mt-1 pl-">{{ risk.description | translate }}</p>
              <sweet-textarea :disableValidation="true" v-else v-model="localRisk.description" :rows="3" class="mt-2" />
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-4">
            <div class="text-break">
              <span class="font-weight-bold">{{ $t("risk.threat-definition.text") }} <span v-if="isEditMode" class="text-danger">*</span> :</span>

              <p v-if="!isEditMode" class="mb-0 mt-1 pl-">{{ displayThreats }}</p>
              <div v-if="isEditMode" class="p-2">
                <right-panel
                  class="float-left"
                  id="sidebar-threat-definition"
                  width="600px"
                  :icon="'mdi mdi-plus'"
                  :text="$t('risk.threat-definition.new')"
                  @hidden="rightPanelClosed"
                >
                  <div class="pl-5 pr-5">
                    <OrganizationRiskThreatDefinitionForm @onSave="onThreatSaved" :hide-library="true" />
                  </div>
                </right-panel>
              </div>
              <div v-if="isEditMode" class="p-2">
                <filter-select
                  id="threatFilter"
                  ref="threatFilter"
                  :limit="3"
                  :placeholder="$t('risk.threat-definition.text')"
                  :multiple="true"
                  @filter="onThreatDefinitionSearch"
                  :selected="localRisk.threats"
                  @changed="onThreatDefinitionSelect"
                />
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="text-break">
              <span class="font-weight-bold">{{ $t("risk.weakness-definition.text") }} <span v-if="isEditMode" class="text-danger">*</span> :</span>

              <p v-if="!isEditMode" class="mb-0 mt-1 pl-">{{ displayWeaknesses }}</p>
              <div v-if="isEditMode" class="p-2">
                <right-panel
                  class="float-left"
                  id="sidebar-weakness-definition"
                  width="600px"
                  :icon="'mdi mdi-plus'"
                  :text="$t('risk.weakness-definition.new')"
                  @hidden="rightPanelClosed"
                >
                  <div class="pl-5 pr-5">
                    <OrganizationRiskWeaknessDefinitionForm @onSave="onWeaknessSaved" :hide-library="true" />
                  </div>
                </right-panel>
              </div>
              <div v-if="isEditMode" class="p-2">
                <filter-select
                  id="weaknessFilter"
                  ref="weaknessFilter"
                  :limit="3"
                  :placeholder="$t('risk.weakness-definition.text')"
                  :multiple="true"
                  @filter="onWeaknessDefinitionSearch"
                  :selected="localRisk.weaknesses"
                  @changed="onWeaknessDefinitionSelect"
                />
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="text-break">
              <span class="font-weight-bold">{{ $t("risk.source.text") }} <span v-if="isEditMode" class="text-danger">*</span> :</span>

              <p v-if="!isEditMode" class="mb-0 mt-1 pl-">{{ displaySources }}</p>
              <div v-if="isEditMode" class="p-2">
                <right-panel class="float-left" id="sidebar-threat-source" width="600px" :icon="'mdi mdi-plus'" :text="$t('risk.threat-source.new')" @hidden="rightPanelClosed">
                  <div class="pl-5 pr-5">
                    <OrganizationRiskSourceForm @onSave="onSourceSaved" :hide-library="true" />
                  </div>
                </right-panel>
              </div>
              <div v-if="isEditMode" class="p-2">
                <filter-select
                  id="sourceFilter"
                  ref="sourceFilter"
                  :limit="3"
                  :placeholder="$t('risk.threat-source.text')"
                  :multiple="true"
                  @filter="onSourceSearch"
                  :selected="localRisk.sources"
                  @changed="onSourceSelect"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- category - type - kind -->
        <div class="row mb-3">
          <div class="col-md-4">
            <div class="text-break">
              <span class="font-weight-bold">{{ $t("risk.category.text") }} <span v-if="isEditMode" class="text-danger">*</span> :</span>

              <p v-if="!isEditMode" class="mb-0 mt-1 pl-">
                <span v-if="risk.category">{{ risk.category.name | translate }}</span
                ><span v-else>-</span>
              </p>
              <div v-if="isEditMode" class="p-2">
                <right-panel class="float-left" id="sidebar-category" width="600px" :icon="'mdi mdi-plus'" :text="$t('risk.category.new')" @hidden="rightPanelClosed">
                  <div class="pl-5 pr-5">
                    <OrganizationRiskCategoryForm @onSave="onCategorySaved" :hide-library="true" />
                  </div>
                </right-panel>
              </div>
              <div v-if="isEditMode" class="p-2">
                <filter-select
                  id="categoryFilter"
                  ref="categoryFilter"
                  :limit="3"
                  :placeholder="$t('risk.category.text')"
                  :multiple="false"
                  @filter="onCategorySearch"
                  :selected="localRisk.category"
                  @changed="onCategorySelect"
                />
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="text-break">
              <span class="font-weight-bold">{{ $t("risk.type.text") }} <span v-if="isEditMode" class="text-danger">*</span> :</span>

              <p v-if="!isEditMode" class="mb-0 mt-1 pl-">
                <span v-if="risk.type">{{ risk.type.name | translate }}</span
                ><span v-else>-</span>
              </p>
              <div v-if="isEditMode" class="p-2">
                <right-panel class="float-left" id="sidebar-type" width="600px" :icon="'mdi mdi-plus'" :text="$t('risk.type.new')" @hidden="rightPanelClosed">
                  <div class="pl-5 pr-5">
                    <OrganizationRiskTypeForm @onSave="onTypeSaved" :hide-library="true" />
                  </div>
                </right-panel>
              </div>
              <div v-if="isEditMode" class="p-2">
                <filter-select
                  id="typeFilter"
                  ref="typeFilter"
                  :limit="3"
                  :placeholder="$t('risk.type.text')"
                  :multiple="false"
                  @filter="onTypeSearch"
                  :selected="localRisk.type"
                  @changed="onTypeSelect"
                />
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="text-break">
              <span class="font-weight-bold">{{ $t("risk.kind.text") }} :</span>

              <p v-if="!isEditMode" class="mb-0 mt-1 pl-">
                <span v-if="risk.kind">{{ risk.kind.name | translate }}</span
                ><span v-else>-</span>
              </p>
              <div v-if="isEditMode" class="p-2">
                <right-panel class="float-left" id="sidebar-kind" width="600px" :icon="'mdi mdi-plus'" :text="$t('risk.kind.new')" @hidden="rightPanelClosed">
                  <div class="pl-5 pr-5">
                    <OrganizationRiskKindForm @onSave="onKindSaved" :hide-library="true" />
                  </div>
                </right-panel>
              </div>
              <div v-if="isEditMode" class="p-2">
                <filter-select
                  id="kindFilter"
                  ref="kindFilter"
                  :limit="3"
                  :placeholder="$t('risk.kind.text')"
                  :multiple="false"
                  @filter="onKindSearch"
                  :selected="localRisk.kind"
                  @changed="onKindSelect"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- owner -->
        <div class="row mb-3">
          <div class="col-md-4">
            <span class="font-weight-bold">{{ $t("risk.level.text") }} <span v-if="isEditMode" class="text-danger">*</span> :</span>
            <span v-if="!isEditMode">
              <span v-if="localRisk.owner && localRisk.owner.organization">{{ $t("risk.level.corporate") }}</span>
              <span v-if="localRisk.owner && localRisk.owner.department">{{ $t("department.text") }}</span>
              <span v-if="localRisk.owner && localRisk.owner.process">{{ $t("process.text") }}</span>
            </span>
            <select v-else @change="ownerChanded" class="form-control mt-1" v-model="selectedOwnerType">
              <option value="ORGANIZATION">{{ $t("audit-control.control-level.corporate") }}</option>
              <option value="DEPARTMENT">{{ $t("department.text") }}</option>
              <option value="PROCESS">{{ $t("process.text") }}</option>
            </select>
          </div>
          <div class="col-md-4">
            <span v-if="localRisk.owner && localRisk.owner.organization" class="font-weight-bold">{{ $t("organization.name") }}:</span>
            <span v-if="localRisk.owner && localRisk.owner.department" class="font-weight-bold">{{ $t("department.name") }}:</span>
            <span v-if="localRisk.owner && localRisk.owner.process" class="font-weight-bold">{{ $t("process.name") }}:</span>
            <div v-if="!isEditMode">
              <span v-if="localRisk.owner && localRisk.owner.organization"> {{ localRisk.owner.organization.name }}</span>
              <span v-if="localRisk.owner && localRisk.owner.department"> {{ localRisk.owner.department.name | translate }}</span>
              <span v-if="localRisk.owner && localRisk.owner.process"> {{ localRisk.owner.process.name | translate }}</span>
            </div>
            <div v-else>
              <div v-if="selectedOwnerType == 'ORGANIZATION'">
                <filter-select
                  disabled
                  v-show="currentOrganization"
                  class="mt-1"
                  :selected="{ _id: currentOrganization._id, name: { tr: currentOrganization.name, en: currentOrganization.name } }"
                  :placeholder="$t('organization.text')"
                  :multiple="false"
                  :custom-label="customLabel"
                />
              </div>
              <div v-show="selectedOwnerType == 'DEPARTMENT'">
                <filter-select
                  class="mt-1"
                  ref="departmentFilter"
                  :selected="localRisk.owner ? localRisk.owner.department : null"
                  :placeholder="$t('department.text')"
                  :multiple="false"
                  @filter="onDepartmentSearch"
                  @changed="onDepartmentSelect"
                />
              </div>
              <div v-show="selectedOwnerType == 'PROCESS'">
                <filter-select
                  class="mt-1"
                  ref="processFilter"
                  @filter="onProcessSearch"
                  :selected="localRisk.owner ? localRisk.owner.process : null"
                  :placeholder="$t('process.text')"
                  :multiple="false"
                  @changed="onProcessSelect"
                />
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group mb-0 mt-3">
              <span class="font-weight-bold">{{ $t("risk.admin") }} :</span>
              <span v-if="!isEditMode">{{ displayAdmin }}</span>
              <div v-else class="mt-1">
                <organization-member-select :selected="localRisk.admin" @onSelect="(item) => (localRisk.admin = item)" :organization="currentOrganization" />
              </div>
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-4">
            <div class="form-group mb-0">
              <span class="font-weight-bold">{{ $t("risk.reporting-person") }} :</span>
              <span v-if="!isEditMode">{{ risk.reportingPerson || "-" }}</span>
              <sweet-input v-else v-model="localRisk.reportingPerson" class="mt-1" :disableValidation="true" />
            </div>
          </div>
        </div>

        <b-card no-body class="custom-card shadow-none custom-accordion mb-3">
          <b-card-header header-tag="header" role="tab" class="bg-white shadow-sm border-bottom-0">
            <a v-b-toggle="'accordion-compliances'" class="text-primary d-block font-weight-bold" href="javascript: void(0);">
              <h5 class="m-0"><i class="bx bx-check-circle mr-2"></i>{{ $t("policy.related-compliances") }}</h5>
            </a>
          </b-card-header>
          <b-collapse id="accordion-compliances" role="tabpanel" v-model="accordionState.compliances">
            <b-card-body>
              <div class="text-right mb-2">
                <b-button
                  :disabled="risk.status !== 'ACTIVE'"
                  variant="outline-primary"
                  size="sm"
                  title="Sadece Aktif Statüsündeki Riskler İçin Uyumluluk Eklenebilir"
                  @click="$bvModal.show('compliance-item-modal-select')"
                >
                  <i class="bx bx-plus"></i> {{ $t("common.add") }}
                </b-button>
              </div>

              <table class="table table-bordered table-striped table-light">
                <thead class="bg-soft-secondary">
                  <tr>
                    <td class="w-10">{{ $t("compliance-item.compliance") }}</td>
                    <td class="w-10">{{ $t("compliance-item-group.text") }}</td>
                    <td class="w-10">{{ $t("compliance-item.no") }}</td>
                    <td class="w-10">{{ $t("compliance-item.level") }}</td>
                    <td class="w-25">{{ $t("compliance-item.name") }}</td>
                    <td class="w-30">{{ $t("compliance-item.description") }}</td>
                    <td class="w-5"></td>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in localRisk.complianceItems" :key="item._id">
                    <td>{{ item.compliance }}</td>
                    <td>
                      <span v-if="item.group">{{ item.group.name | translate }}</span>
                    </td>
                    <td>{{ item.no }}</td>
                    <td>{{ item.level }}</td>
                    <td>{{ item.name | translate }}</td>
                    <td>{{ item.description | translate }}</td>
                    <td>
                      <i v-if="risk.status === 'ACTIVE'" class="bx bx-trash hand text-danger font-size-18" @click="removeComplianceItemDirect(item._id)"></i>
                    </td>
                  </tr>
                  <tr v-if="localRisk.complianceItems && localRisk.complianceItems.length == 0">
                    <td colspan="7" class="text-center">{{ $t("common.no-data") }}</td>
                  </tr>
                </tbody>
              </table>
            </b-card-body>
          </b-collapse>
        </b-card>

        <!-- Accordions -->
        <!-- 1. analysis Section -->
        <b-card no-body class="custom-card shadow-none custom-accordion mb-3">
          <b-card-header header-tag="header" role="tab" class="bg-white shadow-sm border-bottom-0">
            <a v-b-toggle="'accordion-analysis'" class="text-primary d-block font-weight-bold" href="javascript: void(0);">
              <h5 class="m-0"><i class="bx bx-info-circle mr-2"></i>{{ $t("risk.analysis") }}</h5>
            </a>
          </b-card-header>
          <b-collapse id="accordion-analysis" role="tabpanel" v-model="accordionState.analysis">
            <b-card-body>
              <div class="text-right mb-2">
                <b-button :disabled="risk.status !== 'ACTIVE'" variant="outline-primary" size="sm" @click="$bvModal.show('control-select')">
                  <i class="bx bx-plus"></i> {{ $t("common.add") }}
                </b-button>
              </div>

              <h5 class="m-0"><i class="bx bx-info-circle mr-2"></i>{{ $t("risk.related-controls.text") }}</h5>
              <AuditControlSelectModal
                id="control-select"
                :title="$t('audit-control.control.add')"
                :hide-created-button="true"
                :add-button-text="$t('audit-control.select')"
                :pre-selected-ids="localRisk && localRisk.controls ? localRisk.controls.map((c) => c._id) : []"
                @selected="addControls"
              />
              <table class="table table-bordered table-striped">
                <thead class="">
                  <tr>
                    <th>{{ $t("audit-control.control.no") }}</th>
                    <th>{{ $t("audit-control.control.name") }}</th>
                    <th>{{ $t("audit-control.control.category") }}</th>
                    <th>{{ $t("audit-control.control.status") }}</th>
                    <th>{{ $t("audit-control.control.last-test") }}</th>
                    <th style="width: 50px;"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="control in localRisk.controls" :key="control._id" class="cursor-pointer">
                    <td>{{ control.no }}</td>
                    <td>{{ control.name | translate }}</td>
                    <td>
                      <span v-if="control.category">{{ control.category.name | translate }}</span>
                    </td>
                    <td>{{ control.status ? $t("audit-control.status." + control.status.toLowerCase()) : "" }}</td>
                    <td>
                      <div v-if="control.lastTest" class="small">
                        <div class="font-weight-bold">{{ control.lastTest.auditName | translate }}</div>
                        <div>{{ control.lastTest.testDate | date }}</div>
                        <div v-if="control.lastTest.result">
                          <b-badge :variant="control.lastTest.result === 'EFFECTIVE' ? 'success' : 'danger'">
                            {{ $t("audit-control.test.result." + control.lastTest.result.toLowerCase()) }}
                          </b-badge>
                          <span class="ml-1 text-muted" v-if="control.lastTest.findingCount > 0">
                            ({{ control.lastTest.findingCount }} {{ $t("audit-control.finding.title") }})
                          </span>
                        </div>
                      </div>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td class="text-center" @click.stop>
                      <i v-if="risk.status === 'ACTIVE'" class="bx bx-trash hand text-danger font-size-18" @click="removeControl(control)"></i>
                    </td>
                  </tr>
                  <tr v-if="!localRisk || !localRisk.controls || !localRisk.controls.length">
                    <td colspan="6" class="text-center">{{ $t("common.no-data") }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="text-right mb- mt-4">
                <b-button class="text-right" :disabled="risk.status !== 'ACTIVE'" variant="outline-primary" size="sm" v-b-toggle.sidebar-new-asset-group>
                  <i class="bx bx-plus"></i> {{ $t("common.add") }}
                </b-button>
              </div>
              <h5 class="m-0 mt-4"><i class="bx bx-server mr-2"></i>{{ $t("risk.related-asset-groups") }}</h5>

              <table class="table table-bordered table-striped mt-2">
                <thead class="">
                  <tr>
                    <th>{{ $t("asset-group-category.text") }}</th>
                    <th>{{ $t("asset-group.text") }}</th>
                    <th>{{ $t("risk.criticality") }}</th>
                    <th>{{ $t("risk.possibility") }}</th>
                    <th>{{ $t("risk.effect") }}</th>
                    <th>{{ $t("risk.point") }}</th>
                    <th>{{ $t("common.description") }}</th>
                    <th style="width: 80px;"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="relation in localRisk.riskAssetGroupRelations" :key="relation.rel_id">
                    <td>{{ relation.assetGroup && relation.assetGroup.assetGroupCategory && relation.assetGroup.assetGroupCategory.name | translate }}</td>
                    <td>{{ relation.assetGroup && relation.assetGroup.name | translate }}</td>
                    <td>{{ relation.assetGroup && relation.assetGroup.criticalityLevel }}</td>
                    <td>{{ relation.possibility }}</td>
                    <td>{{ relation.effectValue }}</td>
                    <td>{{ relation.riskPoint }}</td>
                    <td>{{ relation.description | translate }}</td>
                    <td class="text-center" @click.stop>
                      <i v-if="risk.status === 'ACTIVE'" class="bx bx-pencil hand text-primary font-size-18 mr-2" @click="editAssetGroupRelation(relation)"></i>
                      <i v-if="risk.status === 'ACTIVE'" class="bx bx-trash hand text-danger font-size-18" @click="removeAssetGroupRelation(relation)"></i>
                    </td>
                  </tr>
                  <tr v-if="!localRisk || !localRisk.riskAssetGroupRelations || !localRisk.riskAssetGroupRelations.length">
                    <td colspan="8" class="text-center">{{ $t("common.no-data") }}</td>
                  </tr>
                </tbody>
              </table>
              <b-sidebar id="sidebar-new-asset-group" right shadow width="600px" backdrop @hidden="onAssetGroupSaved">
                <div class="px-3 py-2">
                  <RiskAssetGroupRelationForm :risk="localRisk" @onAdd="onAssetGroupSaved" />
                </div>
              </b-sidebar>

              <div class="row">
                <div class="col-md-4">
                  <h6>
                    {{ $t("risk.possibility") }}
                    <i id="popover-possibility" class="bx bx-question-mark rounded-circle ml-2 bg-primary text-white"></i>
                  </h6>

                  <b-popover container="popover-info" custom-class="wide-popover" target="popover-possibility" triggers="hover focus" placement="right">
                    <!-- <template #title>İstatistikler</template> -->
                    <table class="table mb-0">
                      <thead>
                        <tr>
                          <th>{{ $t("risk.possibility") }}</th>
                          <th>{{ $t("risk.info.possibility.title") }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="level in [1, 2, 3, 4, 5]" :key="`possibility-${level}`">
                          <td>
                            <p class="mb-1">{{ level }}-{{ $t(getAppliedRiskValueKey(level)) }}</p>
                          </td>
                          <td>
                            <p v-for="(line, idx) in getPossibilityLines(level)" :key="`possibility-${level}-${idx}`" class="mb-1">{{ line }}</p>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </b-popover>

                  <select :disabled="risk.status !== 'ACTIVE' || hasAssetGroups" class="form-control" v-model="localRisk.possibility">
                    <option :value="1">1-{{ $t("risk.applied-risk-value.lowest") }} </option>
                    <option :value="2">2-{{ $t("risk.applied-risk-value.low") }}</option>
                    <option :value="3">3-{{ $t("risk.applied-risk-value.medium") }}</option>
                    <option :value="4">4-{{ $t("risk.applied-risk-value.high") }}</option>
                    <option :value="5">5-{{ $t("risk.applied-risk-value.highest") }}</option>
                  </select>
                </div>
                <div class="col-md-4">
                  <div>
                    <h6 style="display: inline-block;">
                      {{ $t("risk.effect") }}
                    </h6>

                    <i id="popover-effect" class="bx bx-question-mark rounded-circle ml-2 bg-primary text-white"></i>
                    <b-popover container="popover-info" custom-class="wide-popover" target="popover-effect" triggers="hover focus" placement="right">
                      <!-- <template #title>İstatistikler</template> -->
                      <table class="table mb-0">
                        <thead>
                          <tr>
                            <th>{{ $t("risk.effect") }}</th>
                            <th>{{ $t("risk.info.effect.title") }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="level in [1, 2, 3, 4, 5]" :key="`effect-${level}`">
                            <td>
                              <p class="mb-1">{{ level }}</p>
                            </td>
                            <td>
                              <p class="mb-1">{{ $t(`risk.info.effect.${level}`) }}</p>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </b-popover>

                    <select :disabled="risk.status !== 'ACTIVE' || hasAssetGroups" class="form-control" v-model="localRisk.effectValue">
                      <option :value="1">1</option>
                      <option :value="2">2</option>
                      <option :value="3">3</option>
                      <option :value="4">4</option>
                      <option :value="5">5</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-4">
                  <h6 class="mt-1">
                    {{ $t("risk.risk-point") }}
                    <i id="popover-point" class="bx bx-question-mark rounded-circle ml-2 bg-primary text-white"></i>
                  </h6>

                  <input disabled v-model="localRisk.riskPoint" type="number" min="1" max="50" class="form-control" />
                  <b-popover container="popover-info" custom-class="wide-popover" target="popover-point" triggers="hover focus" placement="right">
                    <!-- <template #title>İstatistikler</template> -->
                    <table class="table mb-0">
                      <thead>
                        <tr>
                          <th>{{ $t("risk.risk-point") }}</th>
                          <th>{{ $t("risk.info.point.title") }}</th>
                        </tr>
                      </thead>
                      <tbody v-if="riskRatingDefinition">
                        <tr>
                          <td class="bg-success">
                            <p class="mb-1">{{ $t("risk.applied-risk-value.low") }}</p>
                            <p class="mb-1">({{ riskRatingDefinition.lowest.min }}-{{ riskRatingDefinition.low.max }})</p>
                          </td>
                          <td>
                            <p class="mb-1">{{ $t(`risk.info.point.1`) }}</p>
                          </td>
                        </tr>
                        <tr>
                          <td class="bg-light-warning">
                            <p class="mb-1">{{ $t("risk.applied-risk-value.medium") }}</p>
                            <p class="mb-1">({{ riskRatingDefinition.medium.min }}-{{ riskRatingDefinition.medium.max }})</p>
                          </td>
                          <td>
                            <p class="mb-1">{{ $t(`risk.info.point.2`) }}</p>
                          </td>
                        </tr>
                        <tr>
                          <td class="bg-danger">
                            <p class="mb-1">{{ $t("risk.applied-risk-value.high") }}</p>
                            <p class="mb-1">({{ riskRatingDefinition.high.min }}-{{ riskRatingDefinition.highest.max }})</p>
                          </td>
                          <td>
                            <p class="mb-1">{{ $t(`risk.info.point.3`) }}</p>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </b-popover>
                </div>
              </div>

              <div class="row">
                <div class="col-md-4">
                  <h6 class="mt-2">{{ $t("risk.applied-risk-value.text") }}</h6>
                  <select disabled class="form-control " v-model="localRisk.appliedRiskValue">
                    <option value="LOWEST">{{ $t("risk.applied-risk-value.lowest") }}</option>
                    <option value="LOW">{{ $t("risk.applied-risk-value.low") }}</option>
                    <option value="MEDIUM">{{ $t("risk.applied-risk-value.medium") }}</option>
                    <option value="HIGH">{{ $t("risk.applied-risk-value.high") }}</option>
                    <option value="HIGHEST">{{ $t("risk.applied-risk-value.highest") }}</option>
                  </select>
                </div>
                <div class="col-md-4 mt-3">
                  <div v-if="localRisk.riskPoint">
                    <h6 class="mt-2">{{ $t("risk.degree.title") }}</h6>
                    <div class="legend">
                      <div v-if="localRisk.degree == 'PREVENT'" class="legend-item">
                        <div class="box bg-danger"></div>
                        <span>{{ $t("risk.degree.prevent") }}</span>
                      </div>
                      <div v-if="localRisk.degree == 'RECOMMEND_PREVENT'" class="legend-item">
                        <div class="box bg-light-warning"></div>
                        <span>{{ $t("risk.degree.recommend-prevent") }}</span>
                      </div>
                      <div v-if="localRisk.degree == 'ACCEPT'" class="legend-item">
                        <div class="box bg-success"></div>
                        <span>{{ $t("risk.degree.accept") }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 mt-3">
                  <h6 class="font-weight-bold">{{ $t("risk.applied-risk-decision.text") }} :</h6>
                  <select :disabled="risk.status !== 'ACTIVE'" class="form-control" v-model="localRisk.riskDecision" @change="onAnalysisChange">
                    <option v-for="d in ['AVOID', 'TRANSFER', 'MINIMIZE']" :key="d" :value="d">
                      {{ $t("risk.applied-risk-decision." + d.toLowerCase()) }}
                    </option>
                  </select>
                </div>

                <div class="col-md-12 text-right mt-4">
                  <b-button variant="primary" @click="onAnalysisChange" :disabled="risk.status !== 'ACTIVE'">
                    <i class="bx bx-calculator mr-1"></i> {{ $t("risk.calculate") }}
                  </b-button>
                </div>

                <!-- Risk Point History Table -->
                <div class="mt-4 col-md-12">
                  <h6 class="font-weight-bold mb-3"><i class="bx bx-history mr-1"></i> {{ $t("risk.point-history") }}</h6>
                  <div class="table-responsive">
                    <table class="table table-centered table-bordered table-nowrap mb-0 small table-hover">
                      <thead class="bg-light">
                        <tr>
                          <th>{{ $t("risk.possibility") }}</th>
                          <th>{{ $t("risk.effect") }}</th>
                          <th>{{ $t("risk.criticality") }}</th>
                          <th>{{ $t("risk.risk-point") }}</th>
                          <th>{{ $t("common.assessed-by") }}</th>
                          <th>{{ $t("common.assessed-at") }}</th>
                          <th>{{ $t("risk.applied-risk-value.text") }}</th>
                          <th>{{ $t("risk.applied-risk-decision.text") }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(ah, idx) in risk.assessmentHistory" :key="idx">
                          <td>{{ ah.possibility }}</td>
                          <td>{{ ah.effectValue }}</td>
                          <td>{{ ah.criticality || "-" }}</td>
                          <td>{{ ah.point }}</td>
                          <td>{{ ah.performedBy ? ah.performedBy.name : "-" }}</td>
                          <td>{{ ah.date | date }}</td>
                          <td>
                            <b-badge v-if="ah.riskValue" :variant="getRiskValueVariant(ah.riskValue)">
                              {{ $t("risk.applied-risk-value." + ah.riskValue.toLowerCase()) }}
                            </b-badge>
                            <span v-else>-</span>
                          </td>
                          <td>
                            <span v-if="ah.decision">
                              {{ $t("risk.applied-risk-decision." + ah.decision.toLowerCase()) }}
                            </span>
                            <span v-else>-</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </b-card-body>
          </b-collapse>
        </b-card>

        <!-- 2. Risk Prevention Section (Action Plan) -->
        <b-card no-body class="custom-card shadow-none custom-accordion mb-3">
          <b-card-header header-tag="header" role="tab" class="bg-white shadow-sm border-bottom-0">
            <a v-b-toggle="'accordion-prevention'" class="text-primary d-block font-weight-bold" href="javascript: void(0);">
              <h5 class="m-0"><i class="bx bx-task mr-2"></i>{{ $t("risk.prevention") }}</h5>
            </a>
          </b-card-header>
          <b-collapse id="accordion-prevention" role="tabpanel" v-model="accordionState.prevention">
            <b-card-body>
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="font-weight-bold m-0">{{ $t("risk.action-plan-tasks") }}</h6>
                <div>
                  <b-button v-if="canAddTasks" variant="primary" size="sm" @click="openTaskSelectModal" class="mr-2"> <i class="bx bx-plus"></i> {{ $t("common.add") }} </b-button>
                  <b-button v-if="canCompleteRisk" variant="success" size="sm" @click="completeRisk">
                    <i class="bx bx-check"></i> {{ $t("risk.action-plan.complete") || "Riski Kapat" }}
                  </b-button>
                </div>
              </div>

              <div v-if="actionPlanTasks && actionPlanTasks.length > 0">
                <div v-for="task in actionPlanTasks" :key="'task-' + task._id" class="mb-2">
                  <task-item
                    :task="task"
                    :isItDefinition="false"
                    :unlink="true"
                    :readonly="false"
                    @unlinked="removeTaskFromActionPlan(task._id)"
                    @removeClicked="removeTaskFromActionPlan($event._id)"
                    @click="openTaskDetailModal(task)"
                  />
                </div>
              </div>
              <div v-else class="text-muted text-center py-4">
                {{ $t("risk.action-plan.no-tasks") || "Aksiyon planı için henüz görev eklenmemiş." }}
              </div>
            </b-card-body>
          </b-collapse>
        </b-card>

        <!-- 3. Risk Acceptance Section -->
        <b-card no-body class="custom-card shadow-none custom-accordion mb-3">
          <b-card-header header-tag="header" role="tab" class="bg-white shadow-sm border-bottom-0">
            <a v-b-toggle="'accordion-acceptance'" class="text-primary d-block font-weight-bold" href="javascript: void(0);">
              <h5 class="m-0"><i class="bx bx-check-shield mr-2"></i>{{ $t("risk.acceptance.approval.title") || "Risk Kabul Kararı Onayı" }}</h5>
            </a>
          </b-card-header>
          <b-collapse id="accordion-acceptance" role="tabpanel" v-model="accordionState.acceptance">
            <b-card-body>
              <!-- Action Box for Acceptance Approval (Top) -->
              <div v-if="risk.status === 'AWAITING_ACCEPTANCE_APPROVAL' && canResolveAcceptanceApproval" class="mb-4">
                <div class="card border-info bg-light">
                  <div class="card-body">
                    <h5 class="card-title mb-3 ">{{ $t("risk.acceptance.approval.title") }}</h5>
                    <div class="row">
                      <div class="col-md-12">
                        <b-form-group :label="$t('audit-control.approval.status.text')" class="font-weight-bold">
                          <b-form-radio-group
                            v-model="approvalAction"
                            :options="[
                              { text: $t('audit-control.approval.status.approved'), value: 'APPROVED' },
                              { text: $t('audit-control.approval.status.rejected'), value: 'REJECTED' },
                            ]"
                            buttons
                            button-variant="outline-primary"
                            name="acceptance-approval-action-radios"
                          ></b-form-radio-group>
                        </b-form-group>
                      </div>
                      <div class="col-md-12">
                        <b-form-group :label="$t('audit-control.approval.approval.response-note')" class="font-weight-bold">
                          <b-form-textarea v-model="approvalNote" :placeholder="$t('common.enterNote')" rows="3"></b-form-textarea>
                        </b-form-group>
                      </div>
                      <div class="col-md-12 text-right">
                        <b-button @click="resolveAcceptanceApproval()" :disabled="isSendingApproval || !approvalAction" :loading="isSendingApproval" variant="primary" class="px-4">
                          {{ $t("common.send") }}
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Kabul Bilgileri ve Tarihçe -->
              <div v-if="risk.riskDecision === 'ACCEPT' || risk.status === 'AWAITING_ACCEPTANCE_APPROVAL'" class="mb-4">
                <div class="card border-info ">
                  <div class="card-body">
                    <h5 class="card-title mb-3"><i class="bx bx-check-shield mr-1"></i> {{ $t("risk.acceptance.info") || "Risk Kabul Bilgileri" }}</h5>
                    <div class="row">
                      <div class="col-md-8">
                        <span class="font-weight-bold">{{ $t("risk.acceptance.reason") }} :</span>
                        <p class="mb-0">{{ risk.acceptanceReason | translate }}</p>
                      </div>
                      <div class="col-md-4">
                        <span class="font-weight-bold">{{ $t("risk.acceptance.duration") }} :</span>
                        <p v-if="risk.periodicAcceptanceDuration" class="mb-0">
                          {{ risk.periodicAcceptanceDuration.value }}
                          {{ $t("audit-control.unit." + (risk.periodicAcceptanceDuration.unit ? risk.periodicAcceptanceDuration.unit.toLowerCase() : "")) }}
                        </p>
                      </div>
                    </div>

                    <!-- Acceptance History Table -->
                    <div v-if="risk.acceptanceApprovals && risk.acceptanceApprovals.length > 0" class="mt-4 pt-3 border-top">
                      <h6 class="mb-3">{{ $t("risk.acceptance.history") || "Kabul Onay Geçmişi" }}</h6>
                      <div class="table-responsive">
                        <table class="table table-sm table-centered table-nowrap mb-0">
                          <thead class="thead-light">
                            <tr>
                              <th>{{ $t("audit-control.approval.status.text") }}</th>
                              <th>{{ $t("audit-control.approval.approval.requested-by") }}</th>
                              <th>{{ $t("audit-control.approval.approval.requested-at") }}</th>
                              <th>{{ $t("audit-control.approval.approval.responded-by") }}</th>
                              <th>{{ $t("audit-control.approval.approval.responded-at") }}</th>
                              <th>{{ $t("audit-control.approval.approval.response-note") }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(approval, index) in risk.acceptanceApprovals" :key="index">
                              <td>
                                <b-badge :variant="getApprovalStatusVariant(approval.status)">
                                  {{ approval.status ? $t("audit-control.approval.status." + approval.status.toLowerCase()) : "" }}
                                </b-badge>
                              </td>
                              <td>{{ approval.requestedBy ? approval.requestedBy.name : "-" }}</td>
                              <td>{{ approval.requestedAt | date }}</td>
                              <td>{{ approval.respondedBy ? approval.respondedBy.name : "-" }}</td>
                              <td>{{ approval.respondedAt | date }}</td>
                              <td>{{ approval.responseNote | translate }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- If not accepted, show option to accept -->
              <div v-if="risk.riskDecision !== 'ACCEPT' && risk.status !== 'AWAITING_ACCEPTANCE_APPROVAL' && risk.status === 'ACTIVE'">
                <div class="alert alert-warning">
                  {{
                    $t("risk.acceptance.request-description") ||
                      "Bu riski mevcut haliyle kabul etmek istiyorsanız gerekli bilgileri doldurarak 'Kabul Et' butonuna tıklayarak onay sürecini başlatabilirsiniz."
                  }}
                </div>
                <div class="mt-3">
                  <b-form-group :label="$t('risk.acceptance.reason')">
                    <sweet-input :disableValidation="true" v-model="localRisk.acceptanceReason" />
                  </b-form-group>

                  <b-form-group :label="$t('risk.acceptance.duration')">
                    <div class="d-flex">
                      <input type="number" v-model.number="localRisk.periodicAcceptanceDuration.value" class="form-control mr-2" style="flex: 1" min="1" />
                      <select v-model="localRisk.periodicAcceptanceDuration.unit" class="form-control" style="flex: 1">
                        <option value="WEEK">{{ $t("audit-control.unit.week") }}</option>
                        <option value="MONTH">{{ $t("audit-control.unit.month") }}</option>
                        <option value="YEAR">{{ $t("audit-control.unit.year") }}</option>
                      </select>
                    </div>
                  </b-form-group>

                  <div class="text-right mt-3">
                    <b-button variant="success" @click="acceptRisk" :disabled="!canAcceptRisk" class="px-4">
                      <i class="bx bx-check-double mr-1"></i> {{ $t("risk.applied-risk-decision.accept") }}
                    </b-button>
                  </div>
                </div>
              </div>
            </b-card-body>
          </b-collapse>
        </b-card>

        <!-- Accordions -->

        <!-- 3. Approval Section (Identical to Control) -->
        <b-card no-body class="custom-card shadow-none custom-accordion mb-3">
          <b-card-header header-tag="header" role="tab" class="bg-white shadow-sm border-bottom-0">
            <a v-b-toggle="'accordion-approval'" class="text-primary d-block font-weight-bold" href="javascript: void(0);">
              <h5 class="m-0"><i class="bx bx-user-check mr-2"></i>{{ $t("audit-control.approval.status.text") }}</h5>
            </a>
          </b-card-header>
          <b-collapse id="accordion-approval" role="tabpanel" v-model="accordionState.approval">
            <b-card-body>
              <!-- Send for Approval Box -->
              <div v-if="['DETECTED', 'EDIT_REQUESTED_EVALUATING', 'REJECTED_EVALUATING'].includes(risk.status)" class="mb-4">
                <div class="card border-light bg-light">
                  <div class="card-body">
                    <h5 class="card-title mb-3">{{ $t("audit-control.approval.send") }}</h5>
                    <div class="row">
                      <div class="col-md-12" v-if="calculatedTargetApprovalDate">
                        <div class="alert alert-info py-2">
                          <i class="bx bx-info-circle mr-1"></i>
                          {{ $t("risk.approval.target-approval-date") }}: <b>{{ calculatedTargetApprovalDate | date }}</b>
                        </div>
                      </div>
                      <div class="col-md-12">
                        <b-form-group :label="$t('risk.approval.request-note') || 'Not'" class="font-weight-bold">
                          <b-form-textarea v-model="approvalNote" :placeholder="$t('common.enterNote')" rows="3"></b-form-textarea>
                        </b-form-group>
                      </div>
                      <div class="col-md-12 text-right">
                        <b-button @click="requestApproval()" :disabled="isSendingApproval" :loading="isSendingApproval" variant="info" class="px-4">
                          <i class="bx bx-send font-size-16 align-middle mr-1"></i> {{ $t("audit-control.approval.send") }}
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Box -->
              <div v-if="risk.status === 'AWAITING_APPROVAL' && canResolveApproval" class="mb-4">
                <div class="card border-light bg-light">
                  <div class="card-body">
                    <h5 class="card-title mb-3">{{ $t("audit-control.approval.status.text") }}</h5>
                    <div class="row">
                      <div class="col-md-12">
                        <b-form-group :label="$t('audit-control.approval.status.text')" class="font-weight-bold">
                          <b-form-radio-group
                            v-model="approvalAction"
                            :options="[
                              { text: $t('audit-control.approval.status.approved'), value: 'APPROVED' },
                              { text: $t('audit-control.approval.status.rejected'), value: 'REJECTED' },
                              { text: $t('audit-control.approval.status.request_edit'), value: 'REQUEST_EDIT' },
                            ]"
                            buttons
                            button-variant="outline-primary"
                            name="approval-action-radios"
                          ></b-form-radio-group>
                        </b-form-group>
                      </div>
                      <div class="col-md-12">
                        <b-form-group :label="$t('audit-control.approval.approval.response-note')" class="font-weight-bold">
                          <b-form-textarea v-model="approvalNote" :placeholder="$t('common.enterNote')" rows="3"></b-form-textarea>
                        </b-form-group>
                      </div>
                      <div class="col-md-12 text-right">
                        <b-button @click="resolveApproval()" :disabled="isSendingApproval || !approvalAction" :loading="isSendingApproval" variant="primary" class="px-4">
                          {{ $t("common.send") }}
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Approval History Table -->
              <div v-if="risk.approvals && risk.approvals.length > 0">
                <h6 class="mb-3 font-weight-bold">{{ $t("audit-control.approval.approval-history") }}</h6>
                <div class="table-responsive">
                  <table class="table table-bordered table-striped table-hover mb-0">
                    <thead class="bg-light">
                      <tr>
                        <th>{{ $t("audit-control.approval.approval.status") }}</th>
                        <th>{{ $t("audit-control.approval.approval.requested-by") }}</th>
                        <th>{{ $t("audit-control.approval.approval.requested-at") }}</th>
                        <th>{{ $t("risk.approval.target-approval-date") }}</th>
                        <th>{{ $t("audit-control.approval.approval.request-note-control") }}</th>
                        <th>{{ $t("audit-control.approval.approval.responded-by") }}</th>
                        <th>{{ $t("audit-control.approval.approval.responded-at") }}</th>
                        <th>{{ $t("audit-control.approval.approval.response-note-control") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(approval, index) in risk.approvals" :key="index">
                        <td>
                          <b-badge :variant="getApprovalStatusVariant(approval.status)">
                            {{ approval.status ? $t("audit-control.approval.status." + approval.status.toLowerCase()) : "" }}
                          </b-badge>
                        </td>
                        <td>{{ approval.requestedBy ? approval.requestedBy.name : "-" }}</td>
                        <td>{{ approval.requestedAt | date }}</td>
                        <td>{{ (approval.targetApprovalDate || getTargetApprovalDate(approval)) | date }}</td>
                        <td>{{ approval.requestNote | translate }}</td>
                        <td>
                          <span v-if="approval.respondedBy">{{ approval.respondedBy.name }}</span>
                          <span v-else>-</span>
                        </td>
                        <td>
                          <span v-if="approval.respondedAt">{{ approval.respondedAt | date }}</span>
                          <span v-else>-</span>
                        </td>
                        <td>{{ approval.responseNote | translate }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else class="text-center text-muted p-3">
                {{ $t("common.no-data") }}
              </div>
            </b-card-body>
          </b-collapse>
        </b-card>

        <!-- 3.5. Files -->
        <EntityFileSection v-if="risk._id" entityType="risk" :entityId="risk._id" :files="risk.files || []" :canUpload="canEdit" uploadBaseUrl="risk" @uploaded="fetchDetail()" />
        <!-- 1.5 Bağlı Bulgular (Collapsible) -->
        <b-card no-body class="custom-card shadow-none custom-accordion mb-3">
          <b-card-header header-tag="header" role="tab" class="bg-white shadow-sm border-bottom-0">
            <a v-b-toggle="'accordion-findings'" class="text-danger d-block font-weight-bold" href="javascript: void(0);">
              <h5 class="m-0"><i class="bx bx-target-lock mr-2"></i>{{ $t("risk.related-findings") }}</h5>
            </a>
          </b-card-header>
          <b-collapse id="accordion-findings" role="tabpanel" v-model="accordionState.findings">
            <b-card-body>
              <div class="d-flex justify-content-end mb-3" v-if="risk.status === 'ACTIVE' || isEditMode">
                <!-- <div class="mr-2" style="width: 300px; text-align: left;">
                  <filter-select
                    ref="findingFilter"
                    @filter="onFindingSearch"
                    @changed="onFindingSelect"
                    :placeholder="$t('audit-finding.title') + ' ' + $t('common.select')"
                    :multiple="false"
                  />
                </div> -->
                <b-button variant="outline-primary" size="sm" style="height: 38px;" v-b-toggle.sidebar-new-risk-finding>
                  <i class="bx bx-plus"></i> {{ $t("common.add") }}
                </b-button>
              </div>

              <table class="table table-bordered table-striped table-light">
                <thead class="bg-soft-secondary">
                  <tr>
                    <th>{{ $t("finding.no") }}</th>
                    <!-- <th>{{ $t("finding.status") }}</th> -->
                    <th>{{ $t("audit-finding.name") }}</th>
                    <th v-if="risk.status === 'ACTIVE' || isEditMode" style="width: 50px;"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="finding in localRisk.relatedFindings" :key="finding._id">
                    <td>{{ finding.no }}</td>
                    <!-- <td>
                      <b-badge variant="secondary">{{ $t("audit-finding.status." + finding.status.toLowerCase()) }}</b-badge>
                    </td> -->
                    <td>{{ finding.name | translate }}</td>
                    <td v-if="risk.status === 'ACTIVE' || isEditMode" class="text-center">
                      <i class="bx bx-trash hand text-danger font-size-18" @click="unlinkFinding(finding._id)"></i>
                    </td>
                  </tr>
                  <tr v-if="!localRisk.relatedFindings || localRisk.relatedFindings.length === 0">
                    <td :colspan="4" class="text-center">{{ $t("common.no-data") }}</td>
                  </tr>
                </tbody>
              </table>

              <b-sidebar id="sidebar-new-risk-finding" :title="$t('audit-finding.new') || 'Yeni Bulgu'" right shadow width="900px" backdrop>
                <div class="p-3" v-if="accordionState.findings">
                  <organization-finding-form :initial-risks="[localRisk._id]" @save="onFindingCreated" @cancel="$root.$emit('bv::toggle::collapse', 'sidebar-new-risk-finding')" />
                </div>
              </b-sidebar>
            </b-card-body>
          </b-collapse>
        </b-card>
        <!-- 4. History Section -->
        <b-card no-body class="custom-card shadow-none custom-accordion mb-3">
          <b-card-header header-tag="header" role="tab" class="bg-white shadow-sm border-bottom-0">
            <a v-b-toggle="'accordion-history'" class="text-primary d-block font-weight-bold" href="javascript: void(0);">
              <h5 class="m-0"><i class="bx bx-history mr-2"></i>{{ $t("risk.history") }}</h5>
            </a>
          </b-card-header>
          <b-collapse id="accordion-history" role="tabpanel" v-model="accordionState.history">
            <b-card-body>
              <OrganizationRiskHistory :history="risk.history" />
            </b-card-body>
          </b-collapse>
        </b-card>
      </div>
    </div>
    <TaskDetail @onSave="onTaskSaved" />
  </div>
</template>

<script>
import PropertyUtil from "@/helpers/property-util";
import AppliedRiskValueBadge from "./component/applied-risk-value-badge";
import OrganizationMemberSelect from "@cc/organization-member-select";
import SweetTextarea from "@c/form/sweet-textarea";
import ComplianceItemSelect from "../audit-control/control/compliance-item-select.vue";
import ModalSelect from "@c/modal-select";
import OrganizationRiskAppliedControlForm from "./applied-control/organization-risk-applied-control-form.vue";
import OrganizationRiskThreatDefinitionForm from "./threat-definition/organization-risk-threat-definition-form.vue";
import OrganizationRiskWeaknessDefinitionForm from "./weakness-definition/organization-risk-weakness-definition-form.vue";
import OrganizationRiskSourceForm from "./source/organization-risk-source-form.vue";
import OrganizationRiskTypeForm from "./type/organization-risk-type-form.vue";
import OrganizationRiskKindForm from "./kind/organization-risk-kind-form.vue";
import OrganizationRiskCategoryForm from "./category/organization-risk-category-form.vue";
import AuditControlSelectModal from "../audit-control/audit/audit-control-select-modal.vue";
import TaskDetail from "@/components/client/task/task-detail.vue";
import rest from "@/rest-client";
import OrganizationRiskHistory from "./organization-risk-history.vue";
import OrganizationFindingForm from "@/modules/finding/organization-finding-form.vue";
import RiskAssetGroupRelationForm from "./risk-asset-group/risk-asset-group-relation-form.vue";
import EntityFileSection from "@c/entity-file-section.vue";

export default {
  components: {
    AuditControlSelectModal,
    AppliedRiskValueBadge,
    OrganizationMemberSelect,
    ComplianceItemSelect,
    SweetTextarea,
    OrganizationRiskAppliedControlForm,
    OrganizationRiskThreatDefinitionForm,
    OrganizationRiskWeaknessDefinitionForm,
    OrganizationRiskSourceForm,
    OrganizationRiskTypeForm,
    OrganizationRiskKindForm,
    OrganizationRiskCategoryForm,
    TaskDetail,
    OrganizationRiskHistory,
    OrganizationFindingForm,
    RiskAssetGroupRelationForm,
    EntityFileSection,
  },
  props: {
    hideGoBack: { type: Boolean, default: false },
  },
  data() {
    return {
      isEditMode: false,
      localRisk: {
        category: null,
        admin: null,
        status: null,
        owner: { organization: null, department: null, process: null },
        threats: [],
        weaknesses: [],
        type: null,
        kind: null,
        source: null,
        possibility: null,
        effectValue: null,
        reportingPerson: null,
        description: null,
        complianceItems: [],
        appliedControls: [],
        riskDegree: null,
        riskDecision: null,
        riskPoint: null,
        acceptanceReason: null,
        periodicAcceptanceDuration: { value: 1, unit: "MONTH" },
      },

      selectedOwnerType: "ORGANIZATION",
      accordionState: {
        analysis: false,
        findings: false,
        prevention: false,
        acceptance: false,
        approval: false,
        compliances: false,
        history: false,
      },
      approvalRequest: { assignee: null, note: "" },
      approvalAction: null,
      approvalNote: null,
      closeNote: undefined,
      cancelNote: undefined,
      isSendingApproval: false,
      selectedAssetGroupRelation: null,
    };
  },
  computed: {
    hasAssetGroups() {
      return this.localRisk && this.localRisk.riskAssetGroupRelations && this.localRisk.riskAssetGroupRelations.length > 0;
    },
    targetConfig() {
      return this.$store.getters["organizationRiskTarget/selectedEntity"];
    },
    riskTargets() {
      return this.targetConfig?.criticality || {};
    },
    riskRatingDefinition() {
      return this.$store.getters[`organizationRisk/riskRatingDefinition`];
    },
    selectedControlIds() {
      return this.localRisk.controls ? [this.localRisk.controls._id] : [];
    },
    risk() {
      return this.$store.getters["organizationRisk/risk"] || {};
    },
    isLoading() {
      return this.$store.getters["organizationRisk/isLoading"];
    },
    isCalculating() {
      return this.$store.getters["organizationRisk/isCalculating"];
    },
    calculatedTargetApprovalDate() {
      const key = this.risk?.appliedRiskValue;
      const targetItem = key && this.riskTargets ? this.riskTargets[key] : null;
      if (targetItem?.approvalDuration) {
        return this.calculateTargetDate(new Date(), targetItem.approvalDuration);
      }
      return null;
    },
    isAwaitingApproval() {
      return this.risk.status === "AWAITING_APPROVAL";
    },
    displayAdmin() {
      return this.risk.admin ? this.risk.admin.name : "-";
    },
    displayStatus() {
      if (!this.risk.status) return "-";
      return this.$t("risk.status." + this.risk.status.toLowerCase());
    },
    displayThreats() {
      if (!this.localRisk?.threats) return "-";
      return this.localRisk.threats.map((t) => this.$options.filters.translate(t.name)).join(", ");
    },
    canAcceptRisk() {
      const reason = this.localRisk.acceptanceReason;
      const durationVal = this.localRisk.periodicAcceptanceDuration?.value;

      return !!reason && !!durationVal;
    },
    displayWeaknesses() {
      if (!this.localRisk?.weaknesses) return "-";
      return this.localRisk.weaknesses.map((t) => this.$options.filters.translate(t.name)).join(", ");
    },
    displaySources() {
      if (!this.localRisk?.sources) return "-";
      return this.localRisk.sources.map((t) => this.$options.filters.translate(t.name)).join(", ");
    },
    statusVariant() {
      if (!this.risk.status) return "primary";
      const map = {
        ACTIVE: "success",
        AWAITING_APPROVAL: "warning",
        AWAITING_ACCEPTANCE_APPROVAL: "warning",
        REJECTED_EVALUATING: "danger",
        EDIT_REQUESTED_EVALUATING: "info",
        CLOSED: "secondary",
        CANCELED: "danger",
      };
      return map[this.risk.status] || "primary";
    },
    canEdit() {
      const editableStatuses = ["ACTIVE", "EDIT_REQUESTED_EVALUATING", "REJECTED_EVALUATING"];
      return editableStatuses.includes(this.risk.status);
    },
    displayOwner() {
      if (!this.risk.owner) return "-";
      if (this.risk.owner.organization) return this.risk.owner.organization.name;
      if (this.risk.owner.department) return this.risk.owner.department.name?.[this.currentLanguage] || this.risk.owner.department.name;
      if (this.risk.owner.process) return this.risk.owner.process.name?.[this.currentLanguage] || this.risk.owner.process.name;
      return "-";
    },
    displayLevel() {
      if (!this.risk.owner) return "-";
      if (this.risk.owner.organization) return this.$t("audit-control.control-level.corporate");
      if (this.risk.owner.department) return this.$t("department.text");
      if (this.risk.owner.process) return this.$t("process.text");
      return "-";
    },
    displayPossibility() {
      if (!this.risk.possibility) return "-";
      return this.risk.possibility + "-" + this.displayRiskLevelText(this.risk.possibility);
    },
    displayRiskPoint() {
      const val = this.isEditMode ? this.localRisk.riskPoint : this.risk.riskPoint;
      return val || "-";
    },
    canResolveApproval() {
      if (this.risk.status !== "AWAITING_APPROVAL") return false;
      const latest = this.risk.approvals && this.risk.approvals.length > 0 ? this.risk.approvals[0] : null;
      if (!latest || !this.currentAccount) return false;
      const assigneeId = latest.assignee ? latest.assignee._id || latest.assignee : null;
      return assigneeId === this.currentAccount._id;
    },
    canResolveAcceptanceApproval() {
      if (this.risk.status !== "AWAITING_ACCEPTANCE_APPROVAL") return false;
      const latest = this.risk.acceptanceApprovals && this.risk.acceptanceApprovals.length > 0 ? this.risk.acceptanceApprovals[0] : null;
      if (!latest || !this.currentAccount) return false;
      const assigneeId = latest.assignee ? latest.assignee._id || latest.assignee : null;
      return assigneeId === this.currentAccount._id;
    },
    actionPlanTasks() {
      return this.risk.actionPlan || [];
    },
    canAddTasks() {
      return this.risk.status === "ACTIVE";
    },
    canCompleteRisk() {
      if (!this.risk || this.risk.status !== "ACTIVE") return false;
      if (this.risk.admin?._id !== this.currentAccount?._id) return false;
      if (!this.actionPlanTasks || this.actionPlanTasks.length === 0) return false;
      return this.actionPlanTasks.every((task) => task.status === "DONE");
    },
  },
  watch: {
    risk: {
      handler(val) {
        if (val) {
          this.syncLocalRisk(val);
        }
      },
      immediate: true,
    },
  },
  methods: {
    calculatedDueDate() {
      const key = this.localRisk?.appliedRiskValue;
      const targetItem = key && this.riskTargets ? this.riskTargets[key] : null;
      if (targetItem?.resolutionDuration) {
        const date = this.calculateTargetDate(new Date(), targetItem.resolutionDuration);
        return date ? date.getTime() : null;
      }
      return new Date().getTime() + 30 * 24 * 60 * 60 * 1000;
    },
    calculateTargetDate(startDate, duration) {
      if (!startDate || !duration || duration.value === null || duration.value === undefined || !duration.unit) {
        return null;
      }

      const { value, unit } = duration;
      const numValue = Number(value);
      const date = new Date(startDate);
      const upperUnit = unit.toUpperCase();
      if (upperUnit === "WEEK") date.setDate(date.getDate() + numValue * 7);
      else if (upperUnit === "MONTH") date.setMonth(date.getMonth() + numValue);
      else if (upperUnit === "DAY") date.setDate(date.getDate() + numValue);

      return date;
    },
    getTargetApprovalDate(approval) {
      const key = this.risk?.appliedRiskValue;
      const targetItem = key && this.riskTargets ? this.riskTargets[key] : null;
      if (!targetItem?.approvalDuration) return null;
      return this.calculateTargetDate(approval.requestedAt, targetItem.approvalDuration);
    },

    async fetchRiskRatingDefinition() {
      const data = await this.$store.dispatch(`organizationRisk/fetchRiskRatingDefinition`);
      this.$store.commit("organizationRisk/editedRatingDefinition", { ...data });
    },

    getdecisionLines(level) {
      const raw = this.$t(`risk.info.decision.${level}`);

      if (!raw || typeof raw !== "string") {
        return [];
      }

      return raw
        .split(/\n+/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
    },
    getPossibilityLines(level) {
      const raw = this.$t(`risk.info.possibility.${level}`);

      if (!raw || typeof raw !== "string") {
        return [];
      }

      return raw
        .split(/\n+/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
    },
    getAppliedRiskValueKey(level) {
      const map = {
        1: "risk.applied-risk-value.lowest",
        2: "risk.applied-risk-value.low",
        3: "risk.applied-risk-value.medium",
        4: "risk.applied-risk-value.high",
        5: "risk.applied-risk-value.highest",
      };

      return map[level] || "risk.applied-risk-value.lowest";
    },
    addControls(items) {
      const currentItems = this.localRisk.controls || [];
      const newItems = items.filter((item) => !currentItems.some((existing) => existing._id === item._id));

      this.localRisk.controls = [...currentItems, ...newItems];
      this.onAnalysisChange();
    },
    async removeControl(control) {
      const ok = await this.confirm(this.$t("common.confirm-delete"));
      if (!ok) return;
      this.localRisk.controls = this.localRisk.controls.filter((c) => c._id !== control._id);
      await this.directUpdate();
    },
    async removeComplianceItemDirect(id) {
      try {
        const ok = await this.confirm(this.$t("common.confirm-delete"));
        if (!ok) return;

        // this.localControl.complianceItems = this.localControl.complianceItems.filter((item) => item._id !== id);
        await this.$store.dispatch("organizationRisk/update", this.localRisk);
        this.$toast.success(this.$t("common.success"));

        this.fetchDetail();
      } catch (e) {
        this.$handleHttpErrorByStatusCode(e);
      }
    },
    addComplianceItems(items) {
      const currentItems = this.localRisk.complianceItems || [];
      const newItems = items.filter((item) => !currentItems.some((existing) => existing._id === item._id));
      this.localRisk.complianceItems = [...currentItems, ...newItems];

      // Save directly
      this.saveComplianceItems();
    },
    async saveComplianceItems() {
      try {
        await this.$store.dispatch("organizationRisk/update", this.localRisk);
        this.$toast.success(this.$t("common.success"));
        this.fetchDetail();
      } catch (e) {
        this.$handleHttpErrorByStatusCode(e);
      }
    },
    ownerChanded(e) {
      if (e.target.value == "ORGANIZATION") {
        this.onOrganizationSelect();
      }
      setTimeout(() => {
        this.$refs?.departmentFilter?.reset();
        this.$refs?.processFilter?.reset();
      }, 500);
    },

    customLabel(value) {
      return this.$options.filters.translate(value.name);
    },
    rightPanelClosed() {},
    onThreatSaved() {
      this.$root.$emit("bv::toggle::collapse", "sidebar-threat-definition");
      this.$refs?.threatFilter?.searchChanged("*");
    },
    onWeaknessSaved() {
      this.$root.$emit("bv::toggle::collapse", "sidebar-weakness-definition");
      this.$refs?.weaknessFilter?.searchChanged("*");
    },
    onSourceSaved() {
      this.$root.$emit("bv::toggle::collapse", "sidebar-source");
      this.$refs?.sourceFilter?.searchChanged("*");
    },
    onCategorySaved() {
      this.$root.$emit("bv::toggle::collapse", "sidebar-category");
      this.$refs?.categoryFilter?.searchChanged("*");
    },
    onTypeSaved() {
      this.$root.$emit("bv::toggle::collapse", "sidebar-type");
      this.$refs?.typeFilter?.searchChanged("*");
    },
    onKindSaved() {
      this.$root.$emit("bv::toggle::collapse", "sidebar-kind");
      this.$refs?.kindFilter?.searchChanged("*");
    },

    async onThreatDefinitionSearch(value, callback) {
      const response = await this.$store.dispatch(`riskThreatDefinition/paginate`, {
        options: { limit: 200 },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    onThreatDefinitionSelect(threats) {
      this.localRisk = { ...this.localRisk, threats };
    },
    async onWeaknessDefinitionSearch(value, callback) {
      const response = await this.$store.dispatch(`riskWeaknessDefinition/paginate`, {
        options: { limit: 200 },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    onWeaknessDefinitionSelect(weaknesses) {
      this.localRisk = { ...this.localRisk, weaknesses };
    },
    async onSourceSearch(value, callback) {
      const response = await this.$store.dispatch(`riskSource/paginate`, {
        options: { limit: 200 },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    onSourceSelect(sources) {
      this.localRisk = { ...this.localRisk, sources };
    },
    async onCategorySearch(value, callback) {
      const response = await this.$store.dispatch(`riskCategory/paginate`, {
        options: { limit: 200 },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    onCategorySelect(category) {
      this.localRisk = { ...this.localRisk, category };
    },

    async onTypeSearch(value, callback) {
      const response = await this.$store.dispatch(`riskType/paginate`, {
        options: { limit: 200 },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    onTypeSelect(type) {
      this.localRisk = { ...this.localRisk, type };
    },
    async onKindSearch(value, callback) {
      const response = await this.$store.dispatch(`riskKind/paginate`, {
        options: { limit: 200 },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    onKindSelect(kind) {
      this.localRisk = { ...this.localRisk, kind };
    },
    async fetchDetail() {
      const id = this.$route.params.id;
      if (id) {
        await this.$store.dispatch("organizationRisk/fetchDetail", id);
      }
    },
    syncLocalRisk(val) {
      this.localRisk = this.prepareFormForEdit(val, ["note", "name", "description", "reportingPerson", "acceptanceReason"]);
      if (val.owner) {
        if (val.owner.department) this.selectedOwnerType = "DEPARTMENT";
        else if (val.owner.process) this.selectedOwnerType = "PROCESS";
        else this.selectedOwnerType = "ORGANIZATION";
      }
      this.localRisk.category = val.category;
      this.localRisk.threats = val.threats || [];
      this.localRisk.weaknesses = val.weaknesses || [];
      this.localRisk.type = val.type;
      this.localRisk.kind = val.kind;
      this.localRisk.source = val.source;
      this.localRisk.possibility = val.possibility;
      this.localRisk.effectValue = val.effectValue;
      this.localRisk.riskPoint = val.riskPoint;
      this.localRisk.findingRelations = val.findingRelations || [];
      this.localRisk.complianceItems = val.complianceItems || [];
      // this.localRisk.appliedControls = val.appliedControls || [];
      this.localRisk.riskDegree = val.riskDegree;
      this.localRisk.riskDecision = val.riskDecision;
      // this.localRisk.acceptanceReason = val.acceptanceReason;
      this.localRisk.periodicAcceptanceDuration = val.periodicAcceptanceDuration || { value: 1, unit: "MONTH" };
    },
    enableEditMode() {
      this.syncLocalRisk(this.risk);
      this.isEditMode = true;
    },
    cancelEditMode() {
      this.isEditMode = false;
    },
    goBack() {
      this.$router.push("/risk/view");
    },
    displayRiskLevelText(level) {
      const map = { 1: "lowest", 2: "low", 3: "medium", 4: "high", 5: "highest" };
      return this.$t("risk.applied-risk-value." + (map[level] || "lowest"));
    },
    getRiskValueVariant(val) {
      const map = {
        LOWEST: "success",
        LOW: "success",
        MEDIUM: "warning",
        HIGH: "danger",
        HIGHEST: "danger",
      };
      return map[val] || "secondary";
    },
    displayHistoryActionText(action) {
      return this.$t("risk.history.action." + action.toLowerCase());
    },
    ownerChanged() {
      if (this.selectedOwnerType === "ORGANIZATION") {
        this.localRisk.owner = { organization: this.currentOrganization };
      }
    },
    onDepartmentSelect(val) {
      this.localRisk.owner.department = val;
    },
    onProcessSelect(val) {
      this.localRisk.owner.process = val;
    },
    async onDepartmentSearch(v, cb) {
      const resp = await this.$store.dispatch("organizationDepartment/paginate", { query: { name: { value: v } } });
      cb(resp.data?.docs || []);
    },
    async onProcessSearch(v, cb) {
      const resp = await this.$store.dispatch("organizationProcess/paginate", { query: { name: { value: v } } });
      cb(resp.data?.docs || []);
    },
    async onCategorySearch(v, cb) {
      const resp = await this.$store.dispatch("riskCategory/paginate", { query: { name: { value: v } } });
      cb(resp.data?.docs || []);
    },
    onCategorySelect(val) {
      this.localRisk.category = val;
    },
    async onThreatSearch(v, cb) {
      const resp = await this.$store.dispatch("riskThreatDefinition/paginate", { query: { name: { value: v } } });
      cb(resp.data?.docs || []);
    },
    onThreatSelect(val) {
      this.localRisk.threats = val;
    },
    async onWeaknessSearch(v, cb) {
      const resp = await this.$store.dispatch("riskWeaknessDefinition/paginate", { query: { name: { value: v } } });
      cb(resp.data?.docs || []);
    },
    onWeaknessSelect(val) {
      this.localRisk.weaknesses = val;
    },
    async onRiskSourceSearch(v, cb) {
      const resp = await this.$store.dispatch("riskSource/paginate", { query: { name: { value: v } } });
      cb(resp.data?.docs || []);
    },
    onRiskSourceSelect(val) {
      this.localRisk.source = val;
    },
    async onRiskTypeSearch(v, cb) {
      const resp = await this.$store.dispatch("riskType/paginate", { query: { name: { value: v } } });
      cb(resp.data?.docs || []);
    },
    openTaskDetailModal(task) {
      this.$store.commit("task/showTaskDetailModal", true);
      return this.$store.dispatch("task/fetchDetail", task._id);
    },
    openTaskSelectModal() {
      this.$store.commit("task/showTaskDetailModal", true);
      this.$store.commit("task/isCreating", true);

      const priority =
        this.localRisk.appliedRiskValue === "LOWEST"
          ? "1"
          : this.localRisk.appliedRiskValue === "LOW"
          ? "1"
          : this.localRisk.appliedRiskValue === "MEDIUM"
          ? "2"
          : this.localRisk.appliedRiskValue === "HIGH"
          ? "3"
          : "4";
      const x = this.calculatedDueDate();

      this.$store.commit("task/form", {
        reporter: this.risk.admin,
        duedate: x,
        priority,
        causeds: [this.risk._id],
        caused: {
          entityType: "risk",
          entity: {
            risk: {
              _id: this.risk._id,
              no: this.risk.no,
            },
          },
        },
      });
    },
    async onTaskSaved(task) {
      if (task) {
        await this.addTasksToActionPlan(task);
      }
    },
    async addTasksToActionPlan(task) {
      try {
        if (this.risk.actionPlan && this.risk.actionPlan.find((t) => t.taskId === task._id)) {
          return;
        }
        await rest.post(`/risk/${this.risk._id}/action-plan/tasks`, {
          taskId: task._id,
        });
        await this.fetchDetail();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async removeTaskFromActionPlan(taskId) {
      try {
        await rest.delete(`/risk/${this.risk._id}/action-plan/tasks/${taskId}`);
        this.$toast.success(this.$t("common.success"));
        await this.fetchDetail();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async completeRisk() {
      const confirm = await this.confirm(this.$t("risk.action-plan.complete-confirm") || "Riski kapatmak istediğinize emin misiniz?");
      if (!confirm) return;

      try {
        await rest.post(`/risk/${this.risk._id}/complete`);
        this.$toast.success(this.$t("common.success"));
        await this.fetchDetail();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    onRiskTypeSelect(val) {
      this.localRisk.type = val;
    },
    async onKindSearch(v, cb) {
      const resp = await this.$store.dispatch("riskKind/paginate", { query: { name: { value: v } } });
      cb(resp.data?.docs || []);
    },
    onKindSelect(val) {
      this.localRisk.kind = val;
    },
    complianceItemSelected(selections) {
      this.localRisk.complianceItems = [...selections];
    },
    removeComplianceItem(id) {
      this.localRisk.complianceItems = this.localRisk.complianceItems.filter((item) => item._id !== id);
    },
    async onAppliedControlSearch(v, cb) {
      const resp = await this.$store.dispatch("organizationRiskAppliedControl/paginate", { query: { name: { value: v } } });
      cb(resp.data?.docs || []);
    },
    onAppliedControlSelect(val) {
      this.localRisk.appliedControls = val;
    },

    async calculatePoints() {
      const payload = { ...this.localRisk };
      const { data } = await this.$store.dispatch("organizationRisk/calculate", payload);
      this.localRisk.riskPoint = data.riskPoint;
      this.localRisk.degree = data.degree;
      this.localRisk.appliedRiskValue = data.appliedRiskValue;
    },
    async onAnalysisChange() {
      await this.calculatePoints();
      await this.directUpdate();
    },
    async directUpdate() {
      try {
        const payload = JSON.parse(JSON.stringify(this.localRisk));
        await this.$store.dispatch("organizationRisk/update", payload);
        this.$toast.success(this.$t("common.success"));
        this.fetchDetail();
      } catch (e) {
        this.$handleHttpErrorByStatusCode(e);
      }
    },
    save() {
      if (this.isEditMode) {
        // Validate required fields
        const missing = [];
        if (!this.localRisk.name) missing.push(this.$t("risk.name"));
        if (!this.localRisk.description) missing.push(this.$t("risk.description"));
        if (!this.localRisk.threats || this.localRisk.threats.length === 0) missing.push(this.$t("risk.threat-definition.text"));
        if (!this.localRisk.weaknesses || this.localRisk.weaknesses.length === 0) missing.push(this.$t("risk.weakness-definition.text"));
        if (!this.localRisk.sources || this.localRisk.sources.length === 0) missing.push(this.$t("risk.source.text"));
        if (!this.localRisk.category) missing.push(this.$t("risk.category.text"));
        if (!this.localRisk.type) missing.push(this.$t("risk.type.text"));

        if (missing.length > 0) {
          this.$toast.warning(this.$t("common.validation-error") || "Lütfen * ile işaretli zorunlu alanları doldurunuz.");
          return;
        }
      }
      this.confirmSave();
    },
    async handleApprovalRequestOk(evt) {
      evt.preventDefault();
      if (!this.approvalRequest.assignee) {
        this.$toast.error(this.$t("audit-control.approval.validation.assignee-required"));
        return;
      }
      await this.confirmSave();
      this.$bvModal.hide("approval-request-modal");
    },
    async confirmSave() {
      try {
        const payload = JSON.parse(JSON.stringify(this.localRisk));
        await this.$store.dispatch("organizationRisk/update", payload);
        this.$toast.success(this.$t("common.success"));
        this.isEditMode = false;
        this.fetchDetail();
      } catch (e) {
        this.$handleHttpErrorByStatusCode(e);
      }
    },
    async acceptRisk() {
      try {
        this.isSendingApproval = true;

        // First update the risk details (reason and duration)
        await this.$store.dispatch("organizationRisk/update", this.localRisk);

        // Then request acceptance approval
        const reason = this.localRisk.acceptanceReason;
        const note = typeof reason === "string" ? reason : reason?.[this.$i18n.locale] || reason?.tr || reason?.en || "";

        const approvalPayload = {
          riskId: this.risk._id,
          reporterNote: note,
        };
        await this.$store.dispatch("organizationRisk/requestAcceptanceApproval", approvalPayload);

        this.$toast.success(this.$t("common.success"));
        this.fetchDetail();
      } catch (e) {
        this.$handleHttpErrorByStatusCode(e);
      } finally {
        this.isSendingApproval = false;
      }
    },
    async requestApproval() {
      try {
        this.isSendingApproval = true;
        const approvalPayload = {
          riskId: this.risk._id,
          reporterNote: this.approvalNote,
        };
        await this.$store.dispatch("organizationRisk/requestApproval", approvalPayload);
        this.$toast.success(this.$t("common.success"));
        this.fetchDetail();
        this.approvalNote = null;
      } catch (e) {
        this.$handleHttpErrorByStatusCode(e);
      } finally {
        this.isSendingApproval = false;
      }
    },
    async resolveApproval() {
      try {
        if (!this.approvalAction) return;
        this.isSendingApproval = true;

        await this.$store.dispatch("organizationRisk/saveApproval", {
          riskId: this.risk._id,
          status: this.approvalAction,
          responseNote: this.approvalNote,
        });

        this.$toast.success(this.$t("common.success"));
        this.fetchDetail();
        this.approvalAction = null;
        this.approvalNote = null;
      } catch (e) {
        this.$handleHttpErrorByStatusCode(e);
      } finally {
        this.isSendingApproval = false;
      }
    },
    async resolveAcceptanceApproval() {
      try {
        if (!this.approvalAction) return;
        this.isSendingApproval = true;

        await this.$store.dispatch("organizationRisk/saveAcceptanceApproval", {
          riskId: this.risk._id,
          status: this.approvalAction,
          responseNote: this.approvalNote,
        });

        this.$toast.success(this.$t("common.success"));
        this.fetchDetail();
        this.approvalAction = null;
        this.approvalNote = null;
      } catch (e) {
        this.$handleHttpErrorByStatusCode(e);
      } finally {
        this.isSendingApproval = false;
      }
    },
    openCloseRiskModal() {
      this.closeNote = null;
      this.$bvModal.show("close-risk-modal");
    },
    async handleCloseRiskOk(e) {
      if (!this.closeNote) {
        e.preventDefault();
        this.$toast.error(this.$t("common.required"));
        return;
      }
      try {
        const descriptionObj = {};
        descriptionObj[this.$i18n.locale || "tr"] = this.closeNote;

        await this.$store.dispatch("organizationRisk/close", { id: this.risk._id, description: descriptionObj });
        this.$toast.success(this.$t("common.success"));
        this.fetchDetail();
      } catch (err) {
        this.$handleHttpErrorByStatusCode(err);
      }
    },
    openCancelRiskModal() {
      this.cancelNote = null;
      this.$bvModal.show("cancel-risk-modal");
    },
    async handleCancelRiskOk(e) {
      if (!this.cancelNote) {
        e.preventDefault();
        this.$toast.error(this.$t("common.required"));
        return;
      }
      try {
        const descriptionObj = {};
        descriptionObj[this.$i18n.locale || "tr"] = this.cancelNote;

        await this.$store.dispatch("organizationRisk/cancel", { id: this.risk._id, description: descriptionObj });
        this.$toast.success(this.$t("common.success"));
        this.fetchDetail();
      } catch (err) {
        this.$handleHttpErrorByStatusCode(err);
      }
    },
    redirectToRelatedFiles() {
      this.$router.push({ path: "/organization/file-manager", query: { risk: this.risk._id } });
    },
    getApprovalStatusVariant(status) {
      if (!status) return "secondary";
      switch (status) {
        case "APPROVED":
          return "success";
        case "REJECTED":
          return "danger";
        case "PENDING":
          return "warning";
        case "REQUEST_EDIT":
          return "info";
        default:
          return "secondary";
      }
    },
    getActionVariant(action) {
      const map = { CREATED: "success", UPDATED: "info", APPROVED: "success", REJECTED: "danger", REQUEST_EDIT: "warning" };
      return map[action] || "primary";
    },

    async unlinkFinding(findingId) {
      const confirm = await this.confirm(this.$t("alert.approve"));
      if (!confirm) return;
      try {
        await rest.delete(`/finding/${findingId}/risks/${this.risk._id}`);
        this.$toast.success(this.$t("common.success"));
        await this.fetchDetail();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async linkExistingFinding(findingId) {
      if (!findingId) return;
      try {
        await rest.post(`/finding/${findingId}/risks/${this.risk._id}`);
        this.$toast.success(this.$t("common.success"));
        await this.fetchDetail();
        if (this.$refs.findingFilter) {
          this.$refs.findingFilter.reset();
        }
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async onFindingCreated(finding) {
      if (finding && finding._id) {
        await this.linkExistingFinding(finding._id);
      }
      this.$root.$emit("bv::toggle::collapse", "sidebar-new-risk-finding");
    },
    async onFindingSearch(value, callback) {
      const response = await this.$store.dispatch(`organizationFinding/paginate`, {
        options: { limit: 50 },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    async onFindingSelect(finding) {
      if (!finding) return;
      await this.linkExistingFinding(finding._id);
    },
    editAssetGroupRelation(relation) {
      if (relation.rel_id) {
        this.$store.commit(`organizationRiskAssetGroupRelation/editedEntity`, {
          _id: relation.rel_id,
          assetGroupCategory: relation.assetGroup.category,
          assetGroup: relation.assetGroup,
          possibility: relation.possibility,
          effectValue: relation.effectValue,
          description: relation.description,
        });
        this.$root.$emit("bv::toggle::collapse", "sidebar-new-asset-group");
      }
    },
    async removeAssetGroupRelation(relation) {
      const ok = await this.confirm(this.$t("common.confirm-delete"));
      if (!ok) return;

      if (relation.rel_id) {
        await this.$store.dispatch(`organizationRiskAssetGroupRelation/delete`, relation.rel_id);
      }
      this.fetchDetail();
    },
    onAssetGroupSaved() {
      // this.$root.$emit("bv::toggle::collapse", "sidebar-new-asset-group");
      this.selectedAssetGroupRelation = null;

      this.fetchDetail();
    },
  },
  async mounted() {
    this.fetchDetail();
    this.$store.dispatch("organizationRiskTarget/fetchTarget");
    await this.fetchRiskRatingDefinition();
  },
};
</script>

<style scoped>
.custom-card {
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  overflow: hidden;
}
.custom-accordion .card-header {
  padding: 0.75rem 1.25rem;
  border-bottom: 0;
}
.custom-accordion .card-header a {
  text-decoration: none;
}
.legend {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.box {
  width: 16px;
  height: 16px;
  border-radius: 4px;
}

.bg-light-warning {
  background-color: #fff3cd;
}
</style>
